import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Box, HStack, Button, Flex } from "@chakra-ui/react";
import CDS from "design-system";
import ControlledDataTable from "components/Table/ControlledDataTable";
import { MessageSummary } from "features/Resources/Message/models";
import { useMessageListQuery } from "features/Resources/Message/hooks";
import EmailMessageStatusIcon from "features/Resources/Shared/Components/EmailMessageStatusIcon";
import { basicShortDate } from "helpers/time";
import { Text12 } from "components/Text";
import RecipientTag from "components/Tag/ResourceTargetTag/RecipientTag";
import MessageEmailThreeDotMenu from "features/Resources/Shared/Components/Button/MessageEmailThreeDotMenu";
import IssueTableIcon from "components/Icon/IssueTableIcon";
import { DisplaySenderColumn } from "../DisplayRecipients";
import NewHireJourneyTag from "features/NewHireJourney/Components/NewHireJourneyTag";
import { titleize } from "helpers/string";

const columnHelper = createColumnHelper<MessageSummary>();

interface MessagesTableProps {
  queryString?: string;
  onExpand: (item: MessageSummary) => void;
}

export default function MessagesTable({ queryString = "", onExpand }: MessagesTableProps) {
  const { data, isLoading } = useMessageListQuery(queryString);

  const columns: ColumnDef<MessageSummary, any>[] = [
    columnHelper.accessor((row) => row, {
      id: "issues",
      header: "",
      cell: ({ getValue }) => {
        const message = getValue();
        const showIssues =
          message.status !== "delivered" && message.status !== "skipped" && message.status !== "removed";
        return <Flex align="center">{showIssues ? <IssueTableIcon issueIds={message.issueIds} /> : <Box />}</Flex>;
      },
      meta: {
        sx: {
          paddingRight: 0,
          width: "52px",
          maxWidth: "52px",
          minWidth: "52px",
        },
        headerHStackProps: { justifyContent: "flex-start" },
        cellHStackProps: { justifyContent: "flex-start" },
      },
      enableSorting: false,
    }),
    columnHelper.accessor((row) => row, {
      id: "sendDate",
      header: "Scheduled",
      cell: ({ getValue }) => {
        const message = getValue();
        const timestamp = message.deliveredAt ?? message.deliverAt ?? message.attemptedAt;

        return (
          <HStack spacing={2} align="center" justify="flex-start">
            <EmailMessageStatusIcon status={message.status} />
            <Text12>{timestamp ? basicShortDate(timestamp, message.timezone) : "—"}</Text12>
          </HStack>
        );
      },
      meta: {
        sx: {
          paddingLeft: 0,
          paddingRight: 0,
          width: "96px",
          maxWidth: "96px",
          minWidth: "96px",
        },
        headerHStackProps: { justifyContent: "flex-start" },
        cellHStackProps: { justifyContent: "flex-start" },
      },
      enableSorting: false,
    }),
    columnHelper.accessor((row) => row, {
      header: "Name",
      minSize: 100,
      maxSize: 400,
      cell: ({ getValue }) => (
        <Button variant="unstyled" width="fit-content" onClick={() => onExpand(() => getValue())}>
          <Text12
            wordBreak="break-word"
            textDecorationLine={getValue()?.status === "skipped" ? "line-through" : "none"}
            fontWeight="medium"
            noOfLines={2}
          >
            {getValue()?.name}
          </Text12>
        </Button>
      ),
      meta: {
        sx: {
          paddingLeft: 0,
        },
        headerHStackProps: { justifyContent: "flex-start" },
      },
      enableSorting: false,
    }),

    columnHelper.accessor((row) => row, {
      id: "recipient",
      header: "To",
      cell: ({ getValue }) => {
        const message = getValue();
        return (
          <RecipientTag
            resourceId={message?.recipientId}
            resourceType={message?.recipientType}
            role={message?.recipientRole}
            loadingLabel={message?.recipientRole ? `${titleize(message.recipientRole)} (tbd)` : undefined}
            disabled
          />
        );
      },
      meta: {
        sx: {
          paddingLeft: 0,
          width: "160px",
          maxWidth: "160px",
          minWidth: "160px",
        },
        headerHStackProps: { justifyContent: "flex-start" },
        cellHStackProps: { justifyContent: "flex-start" },
      },
      enableSorting: false,
    }),

    columnHelper.accessor((row) => row, {
      id: "sender",
      header: "Sender",
      cell: ({ getValue }) => {
        const message = getValue();
        return <DisplaySenderColumn senderId={message.senderId} />;
      },
      meta: {
        sx: {
          width: "160px",
          maxWidth: "160px",
          minWidth: "160px",
        },
        headerHStackProps: { justifyContent: "flex-start" },
        cellHStackProps: { justifyContent: "flex-start" },
      },
      enableSorting: false,
    }),

    columnHelper.accessor((row) => row, {
      id: "journey",
      header: "New Hire",
      cell: ({ getValue }) => {
        const message = getValue();
        if (!message.newHireJourneyId) return null;
        return <NewHireJourneyTag journeyId={message.newHireJourneyId} size="md" clickable />;
      },
      meta: {
        sx: {
          width: "160px",
          maxWidth: "160px",
          minWidth: "160px",
        },
        headerHStackProps: { justifyContent: "flex-start" },
        cellHStackProps: { justifyContent: "flex-start" },
      },
      enableSorting: false,
    }),

    // Spacer column to push actions to the right
    columnHelper.accessor((row) => row, {
      id: "spacer",
      header: "",
      cell: () => null,
      minSize: 0,
      meta: {
        sx: {
          paddingLeft: 0,
          paddingRight: 0,
        },
      },
      enableSorting: false,
    }),

    columnHelper.accessor((row) => row, {
      id: "statusActions",
      header: "",
      cell: ({ getValue }) => {
        const message = getValue();
        return (
          <HStack gap="1">
            <MessageEmailThreeDotMenu prefixId={message.prefixId} />
          </HStack>
        );
      },
      meta: {
        sx: {
          width: "99px",
          maxWidth: "99px",
          minWidth: "99px",
        },
        headerHStackProps: { justifyContent: "flex-end" },
        cellHStackProps: { justifyContent: "flex-end" },
      },
      enableSorting: false,
    }),
  ];

  return (
    <ControlledDataTable<MessageSummary>
      data={data || []}
      isLoading={isLoading}
      columns={columns}
      emptyStateComponent={
        <Box textAlign="center" p={4}>
          <CDS.Text16 color="chakra-subtle-text">
            No messages found
          </CDS.Text16>
        </Box>
      }
      sx={{ backgroundColor: "transparent" }}
    />
  );
}
